import React from 'react';
import CodeSnippet from './CodeSnippet';
import {
  DocContainer,
  Title,
  SubTitle,
  Paragraph,
  List,
  ListItem,
  OrderedList,
  OrderedListItem,
  Code,
} from './DocStyles';

const StoreDoc: React.FC = () => {
  return (
    <DocContainer>
      <Title>Gerenciamento de Estado com Redux no SmartEntrega</Title>

      <Paragraph>
        Este documento explica como o gerenciamento de estado é implementado no
        projeto SmartEntrega utilizando Redux e Redux Toolkit.
      </Paragraph>

      <SubTitle>Visão Geral do Redux</SubTitle>
      <Paragraph>
        Redux é uma biblioteca para gerenciamento de estado em aplicações
        JavaScript. Ele centraliza o estado da aplicação em uma única "store",
        tornando mais fácil rastrear mudanças e depurar problemas.
      </Paragraph>
      <Paragraph>
        No SmartEntrega, utilizamos o Redux Toolkit, que simplifica a
        configuração e o uso do Redux, reduzindo o código boilerplate.
      </Paragraph>

      <SubTitle>Estrutura do Redux no SmartEntrega</SubTitle>
      <Paragraph>
        A estrutura do Redux no projeto é organizada da seguinte forma:
      </Paragraph>
      <CodeSnippet
        code={`src/
└── store/
    ├── index.ts              # Configuração da store
    └── slices/               # Slices do Redux
        ├── authSlice.ts      # Estado de autenticação
        └── ...               # Outros slices`}
      />

      <SubTitle>Configuração da Store</SubTitle>
      <Paragraph>
        A store do Redux é configurada no arquivo{' '}
        <Code>src/store/index.ts</Code>:
      </Paragraph>
      <CodeSnippet
        code={`// src/store/index.ts
import { configureStore } from '@reduxjs/toolkit';
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';
import authReducer from './slices/authSlice';
// Importe outros reducers aqui

export const store = configureStore({
  reducer: {
    auth: authReducer,
    // Adicione outros reducers aqui
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});

// Tipos para o dispatch e selector tipados
export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Hooks tipados para usar em componentes
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;`}
      />

      <SubTitle>Slices do Redux</SubTitle>
      <Paragraph>
        Os slices são partes do estado global que contêm o estado, reducers e
        actions relacionados a uma funcionalidade específica.
      </Paragraph>

      <SubTitle>Exemplo: AuthSlice</SubTitle>
      <Paragraph>
        O authSlice gerencia o estado de autenticação da aplicação:
      </Paragraph>
      <CodeSnippet
        code={`// src/store/slices/authSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';

// Estado inicial
interface AuthState {
  user: any | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  resetPasswordSuccess: boolean;
}

const initialState: AuthState = {
  user: loadUserFromLocalStorage(),
  token: localStorage.getItem('acessToken'),
  isAuthenticated: !!localStorage.getItem('acessToken'),
  isLoading: false,
  error: null,
  resetPasswordSuccess: false,
};

// Thunk actions (ações assíncronas)
export const login = createAsyncThunk(
  'auth/login',
  async ({ email, password, empresa }: { email: string; password: string; empresa: string }, { rejectWithValue }) => {
    try {
      // Implementação da lógica de login
      // ...
      
      return {
        user: data,
        token: accessToken
      };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao fazer login. Verifique suas credenciais.');
    }
  }
);

// Definição do slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // Reducers síncronos
    clearError: (state) => {
      state.error = null;
    },
    clearResetPasswordSuccess: (state) => {
      state.resetPasswordSuccess = false;
    },
    loadUserData: (state) => {
      const userData = loadUserFromLocalStorage();
      if (userData) {
        state.user = userData;
      }
    },
  },
  extraReducers: (builder) => {
    // Reducers para ações assíncronas
    builder
      // Login
      .addCase(login.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action: PayloadAction<any>) => {
        state.isLoading = false;
        state.isAuthenticated = true;
        state.user = action.payload.user;
        state.token = action.payload.token;
      })
      .addCase(login.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Outros casos...
  },
});

// Exportando actions e reducer
export const { clearError, clearResetPasswordSuccess, loadUserData } = authSlice.actions;
export default authSlice.reducer;`}
      />

      <SubTitle>Usando o Redux em Componentes</SubTitle>

      <SubTitle>Hooks do Redux</SubTitle>
      <Paragraph>
        Para usar o Redux em componentes funcionais, utilizamos hooks
        personalizados:
      </Paragraph>
      <CodeSnippet
        code={`// src/components/ExampleComponent.tsx
import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../store';
import { fetchData } from '../store/slices/dataSlice';

const ExampleComponent: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, isLoading, error } = useAppSelector((state) => state.data);
  
  useEffect(() => {
    dispatch(fetchData());
  }, [dispatch]);
  
  if (isLoading) return <div>Carregando...</div>;
  if (error) return <div>Erro: {error}</div>;
  
  return (
    <div>
      <h1>Dados</h1>
      <ul>
        {data.map(item => (
          <li key={item.id}>{item.name}</li>
        ))}
      </ul>
    </div>
  );
};

export default ExampleComponent;`}
      />

      <SubTitle>Dispatch de Actions</SubTitle>
      <Paragraph>Para disparar actions, use o hook useAppDispatch:</Paragraph>
      <CodeSnippet
        code={`const dispatch = useAppDispatch();

// Disparar uma action síncrona
dispatch(clearError());

// Disparar uma action assíncrona (thunk)
dispatch(login({ email, password, empresa }))
  .unwrap()
  .then(() => {
    // Sucesso
    navigate('/');
  })
  .catch((error) => {
    // Tratamento de erro
    console.error('Falha no login:', error);
  });`}
      />

      <SubTitle>Acessando o Estado</SubTitle>
      <Paragraph>Para acessar o estado, use o hook useAppSelector:</Paragraph>
      <CodeSnippet
        code={`// Acessar todo o estado de autenticação
const auth = useAppSelector((state) => state.auth);

// Acessar propriedades específicas
const { isAuthenticated, user } = useAppSelector((state) => state.auth);

// Usar o estado em renderização condicional
return (
  <div>
    {isAuthenticated ? (
      <p>Bem-vindo, {user?.nome}</p>
    ) : (
      <p>Por favor, faça login</p>
    )}
  </div>
);`}
      />

      <SubTitle>Criando um Novo Slice</SubTitle>
      <Paragraph>
        Para adicionar um novo slice ao SmartEntrega, siga estes passos:
      </Paragraph>
      <OrderedList>
        <OrderedListItem>
          Crie um novo arquivo na pasta <Code>src/store/slices/</Code>
        </OrderedListItem>
        <OrderedListItem>
          Defina o estado inicial e a interface do estado
        </OrderedListItem>
        <OrderedListItem>
          Crie thunk actions para operações assíncronas
        </OrderedListItem>
        <OrderedListItem>
          Defina o slice com reducers e extraReducers
        </OrderedListItem>
        <OrderedListItem>Exporte as actions e o reducer</OrderedListItem>
        <OrderedListItem>
          Adicione o reducer à store em <Code>src/store/index.ts</Code>
        </OrderedListItem>
      </OrderedList>

      <SubTitle>Exemplo: Criando um Slice para Usuários</SubTitle>
      <CodeSnippet
        code={`// src/store/slices/userSlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { api } from '../../services/api';

// Interfaces
interface User {
  id: string;
  nome: string;
  email: string;
  // Outras propriedades
}

interface UserState {
  users: User[];
  selectedUser: User | null;
  isLoading: boolean;
  error: string | null;
}

// Estado inicial
const initialState: UserState = {
  users: [],
  selectedUser: null,
  isLoading: false,
  error: null,
};

// Thunk actions
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.users.getAll();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao buscar usuários');
    }
  }
);

export const createUser = createAsyncThunk(
  'users/createUser',
  async (userData: Omit<User, 'id'>, { rejectWithValue }) => {
    try {
      const response = await api.users.create(userData);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.message || 'Erro ao criar usuário');
    }
  }
);

// Slice
const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    setSelectedUser: (state, action: PayloadAction<User | null>) => {
      state.selectedUser = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchUsers
      .addCase(fetchUsers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchUsers.fulfilled, (state, action: PayloadAction<User[]>) => {
        state.isLoading = false;
        state.users = action.payload;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      
      // createUser
      .addCase(createUser.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(createUser.fulfilled, (state, action: PayloadAction<User>) => {
        state.isLoading = false;
        state.users.push(action.payload);
      })
      .addCase(createUser.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

// Exportando actions e reducer
export const { setSelectedUser, clearError } = userSlice.actions;
export default userSlice.reducer;`}
      />

      <Paragraph>Depois, adicione o reducer à store:</Paragraph>
      <CodeSnippet
        code={`// src/store/index.ts
import { configureStore } from '@reduxjs/toolkit';
import authReducer from './slices/authSlice';
import userReducer from './slices/userSlice';
// Outros imports...

export const store = configureStore({
  reducer: {
    auth: authReducer,
    users: userReducer,
    // Outros reducers...
  },
  // Resto da configuração...
});`}
      />

      <SubTitle>Boas Práticas com Redux</SubTitle>

      <SubTitle>1. Mantenha o Estado Normalizado</SubTitle>
      <Paragraph>
        Para coleções de dados, é melhor armazenar em formato normalizado (como
        um objeto com IDs como chaves):
      </Paragraph>
      <CodeSnippet
        code={`// Ruim
const state = {
  users: [
    { id: 1, name: 'João' },
    { id: 2, name: 'Maria' }
  ]
};

// Bom
const state = {
  users: {
    byId: {
      '1': { id: 1, name: 'João' },
      '2': { id: 2, name: 'Maria' }
    },
    allIds: ['1', '2']
  }
};`}
      />

      <SubTitle>2. Use Seletores para Acessar o Estado</SubTitle>
      <Paragraph>
        Crie funções seletoras para extrair dados do estado:
      </Paragraph>
      <CodeSnippet
        code={`// src/store/selectors/userSelectors.ts
import { RootState } from '../index';

export const selectAllUsers = (state: RootState) => state.users.users;
export const selectUserById = (state: RootState, userId: string) => 
  state.users.users.find(user => user.id === userId);
export const selectIsLoading = (state: RootState) => state.users.isLoading;

// Uso em componentes
const users = useAppSelector(selectAllUsers);
const isLoading = useAppSelector(selectIsLoading);
const user = useAppSelector(state => selectUserById(state, userId));`}
      />

      <SubTitle>3. Evite Mutações Diretas do Estado</SubTitle>
      <Paragraph>
        O Redux Toolkit usa a biblioteca Immer internamente, que permite
        escrever código que parece mutar o estado, mas na verdade cria um novo
        estado imutável:
      </Paragraph>
      <CodeSnippet
        code={`// Com Redux Toolkit (usando Immer)
const userSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {
    updateUser: (state, action) => {
      const { id, ...changes } = action.payload;
      const user = state.users.find(user => user.id === id);
      if (user) {
        // Parece mutação, mas é seguro com Immer
        Object.assign(user, changes);
      }
    }
  }
});`}
      />

      <SubTitle>4. Use createAsyncThunk para Operações Assíncronas</SubTitle>
      <Paragraph>
        O createAsyncThunk simplifica o tratamento de operações assíncronas:
      </Paragraph>
      <CodeSnippet
        code={`export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.users.getAll();
      return response.data;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);`}
      />

      <SubTitle>5. Organize o Estado por Domínio</SubTitle>
      <Paragraph>
        Divida o estado em slices que representam domínios de negócio:
      </Paragraph>
      <List>
        <ListItem>
          <strong>auth</strong> - Autenticação e usuário atual
        </ListItem>
        <ListItem>
          <strong>users</strong> - Gerenciamento de usuários
        </ListItem>
        <ListItem>
          <strong>entregas</strong> - Dados de entregas
        </ListItem>
        <ListItem>
          <strong>ui</strong> - Estado da interface (modais abertos, etc.)
        </ListItem>
      </List>

      <SubTitle>Depuração do Redux</SubTitle>
      <Paragraph>
        Para depurar o Redux, você pode usar a extensão Redux DevTools:
      </Paragraph>
      <OrderedList>
        <OrderedListItem>
          Instale a extensão Redux DevTools no Chrome
        </OrderedListItem>
        <OrderedListItem>
          A store já está configurada para funcionar com a extensão
        </OrderedListItem>
      </OrderedList>
      <Paragraph>Com a extensão, você pode:</Paragraph>
      <List>
        <ListItem>Ver o estado atual</ListItem>
        <ListItem>Ver o histórico de actions</ListItem>
        <ListItem>Ver como cada action modificou o estado</ListItem>
        <ListItem>
          Viajar no tempo (time travel) para estados anteriores
        </ListItem>
        <ListItem>Exportar/importar o estado</ListItem>
      </List>

      <SubTitle>Conclusão</SubTitle>
      <Paragraph>
        O Redux fornece uma maneira previsível de gerenciar o estado da
        aplicação SmartEntrega. Com o Redux Toolkit, a configuração e o uso do
        Redux são simplificados, reduzindo o código boilerplate e facilitando o
        desenvolvimento.
      </Paragraph>
      <Paragraph>
        Ao seguir as boas práticas descritas neste documento, você pode criar um
        código mais organizado, manutenível e fácil de depurar.
      </Paragraph>
    </DocContainer>
  );
};

export default StoreDoc;
